import { useRef } from 'react';
import { useResourceObserver } from './resourceObserver';
import { ResourceDefinition } from '../resources/types';
import { NoParams } from '../resolvers/definition/resolver';
import {
  applySelector,
  BaseOptions,
  evictStaleSelectKeys,
  initial,
  normalizeResourceOptions,
  ResourceWatchDescriptor,
  SelectorCache,
  SelectOption,
  SkipToken,
  skipped,
} from './shared';
import { ResolverState } from '../resolvers/definition/resolverState';

// Overload: param-less WITH select
export function watch<
  TParams extends NoParams,
  TData,
  TSelected,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions &
    SelectOption<TData, TSelected> & { params?: NoParams | SkipToken }
): ResourceWatchDescriptor<TParams, TSelected, TError>;

// Overload: param-less WITHOUT select
export function watch<
  TParams extends NoParams,
  TData,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options?: BaseOptions & { params?: NoParams | SkipToken }
): ResourceWatchDescriptor<TParams, TData, TError>;

// Overload: param-using WITH select
export function watch<TParams, TData, TSelected, TError extends Error = Error>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions &
    SelectOption<TData, TSelected> & {
      params: { [K in keyof TParams]: TParams[K] } | SkipToken;
    }
): ResourceWatchDescriptor<TParams, TSelected, TError>;

// Overload: param-using WITHOUT select
export function watch<TParams, TData, TError extends Error = Error>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions & {
    params: { [K in keyof TParams]: TParams[K] } | SkipToken;
  }
): ResourceWatchDescriptor<TParams, TData, TError>;

// Implementation
export function watch<TParams, TData, TError extends Error = Error>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options?: BaseOptions & {
    params?: TParams | SkipToken;
    select?: (data: TData) => unknown;
  }
): ResourceWatchDescriptor<TParams, any, TError> {
  const descriptor: ResourceWatchDescriptor<TParams, any, TError> = {
    resource: resource as unknown as ResourceDefinition<TParams, any, TError>,
    ...normalizeResourceOptions(options),
  };

  if (options?.select) {
    descriptor.select = options.select;
  }

  return descriptor;
}

type MapDescriptorsToWatches<
  T extends ResourceWatchDescriptor<any, any, any>[],
> = {
  [K in keyof T]: T[K] extends ResourceWatchDescriptor<
    any,
    infer TData,
    infer TError
  >
    ? ResolverState<TData, TError>
    : never;
};

export const useResources = <
  T extends ResourceWatchDescriptor<any, any, any>[],
>(
  descriptors: [...T]
) => {
  const selectCacheRef = useRef(new Map<string, SelectorCache>());
  // eslint-disable-next-line react-hooks/refs
  evictStaleSelectKeys(selectCacheRef.current, descriptors);

  return useResourceObserver(
    descriptors,
    (ctrl, { resource, params, instanceKey, skip, select }) => {
      if (skip) {
        return skipped;
      }

      const state = ctrl.getState({ resource, params, instanceKey }) ?? initial;

      if (!select) {
        return state;
      }

      const cached = selectCacheRef.current.get(instanceKey);
      const result = applySelector(state, select, cached);
      selectCacheRef.current.set(instanceKey, {
        source: state,
        selector: select,
        result,
      });
      return result;
    }
  ) as MapDescriptorsToWatches<T>;
};
